import { notification } from "antd";
import cuid from "cuid";
import { get } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Loading from "shared/components/Spin";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { normalizeTypologyLabel } from "utils/helpers/leadTypology";
import View from "./View";

const TRANSLATION_KEY = "LIST";

const LABEL_SEPARATOR = ", ";

const toRows = (typologies) =>
  (typologies || []).map(({ id, label }) => ({
    key: id,
    id,
    label,
    initialLabel: label,
  }));

const trimmed = (row) => String(get(row, "label", "")).trim();

const List = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.LEAD_TYPOLOGY);
  const COPY = t(TRANSLATION_KEY, { returnObjects: true });

  const [rows, setRows] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [saving, setSaving] = useState(false);
  const [focusKey, setFocusKey] = useState(null);
  const seeded = useRef(false);

  const { data, loading, refetch } = useQuery(graphql.queries.LEAD_TYPOLOGIES, {
    fetchPolicy: "cache-and-network",
  });

  const seed = (typologies) => {
    setRows(toRows(typologies));
    setRemoved([]);
    setFocusKey(null);
  };

  useEffect(() => {
    if (seeded.current || !data) return;
    seeded.current = true;
    seed(get(data, "leadTypologies.data", []));
  }, [data]);

  const [addLeadTypology] = useMutation(graphql.mutations.CREATE_LEAD_TYPOLOGY);
  const [updateLeadTypology] = useMutation(
    graphql.mutations.UPDATE_LEAD_TYPOLOGY,
  );
  const [deleteLeadTypology] = useMutation(
    graphql.mutations.DELETE_LEAD_TYPOLOGY,
  );

  const created = rows.filter((row) => !row.id && trimmed(row));
  const renamed = rows.filter(
    (row) => row.id && trimmed(row) && trimmed(row) !== row.initialLabel,
  );
  const emptied = rows.filter((row) => row.id && !trimmed(row)).length;
  const isDirty = Boolean(created.length || renamed.length || removed.length);

  const collisions = (() => {
    const seen = new Map();
    const duplicated = new Map();
    rows.forEach((row) => {
      const label = trimmed(row);
      const wording = normalizeTypologyLabel(label);
      if (!wording) return;
      if (seen.has(wording)) duplicated.set(wording, seen.get(wording));
      else seen.set(wording, label);
    });
    return duplicated;
  })();

  const duplicates = collisions.size;
  const duplicateWarning =
    duplicates > 0
      ? t(`${TRANSLATION_KEY}.DUPLICATE`, {
          count: duplicates,
          labels: Array.from(collisions.values()).join(LABEL_SEPARATOR),
        })
      : null;

  useEffect(() => {
    if (!isDirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [isDirty]);

  const onAdd = () => {
    const key = cuid();
    setRows((current) => [
      { key, id: null, label: "", initialLabel: null },
      ...current,
    ]);
    setFocusKey(key);
  };

  const onEdit = (key, label) =>
    setRows((current) =>
      current.map((row) => (row.key === key ? { ...row, label } : row)),
    );

  const onRemove = (key) => {
    const row = rows.find((item) => item.key === key);
    if (get(row, "id")) setRemoved((current) => [...current, row.id]);
    setRows((current) => current.filter((item) => item.key !== key));
  };

  const reload = () =>
    refetch()
      .then((fresh) => seed(get(fresh, "data.leadTypologies.data", [])))
      .catch(() => notification.error({ message: COPY.RELOAD_ERROR }));

  const onSave = async () => {
    if (duplicates > 0) return;

    const steps = [
      ...removed.map(
        (id) => () => deleteLeadTypology({ variables: { where: { id } } }),
      ),
      ...renamed.map(
        (row) => () =>
          updateLeadTypology({
            variables: {
              where: { id: row.id },
              data: { label: trimmed(row) },
            },
          }),
      ),
      ...created.map(
        (row) => () =>
          addLeadTypology({ variables: { data: { label: trimmed(row) } } }),
      ),
    ];

    setSaving(true);
    try {
      let refused = false;

      for (const step of steps) {
        const result = await step();
        if (!get(result, "data")) {
          refused = true;
          break;
        }
      }

      if (!refused) notification.success({ message: COPY.SAVED });
    } catch (error) {
      notification.error({ message: COPY.SAVE_ERROR });
    } finally {
      await reload();
      setSaving(false);
    }
  };

  if (loading && !seeded.current) return <Loading />;

  return (
    <View
      COPY={COPY}
      rows={rows.map((row) => ({
        ...row,
        duplicate: collisions.has(normalizeTypologyLabel(trimmed(row))),
      }))}
      focusKey={focusKey}
      emptied={emptied}
      duplicates={duplicates}
      duplicateWarning={duplicateWarning}
      isDirty={isDirty}
      canDiscard={isDirty || emptied > 0}
      saving={saving}
      onAdd={onAdd}
      onEdit={onEdit}
      onRemove={onRemove}
      onDiscard={reload}
      onSave={onSave}
    />
  );
};

export default List;
